import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicBoardHeader } from '~/_app/(public-board)/_components/PublicBoardHeader';
import { PublicBoardStatsBar } from '~/_app/(public-board)/_components/PublicBoardStatsBar';
import { BOARD_STATS_BAR_HEIGHT } from '~/_components/BoardStatsBar';
import { PublicBoardCustomFieldsPanel } from '~/components/PublicBoard/components/PublicBoardCustomFieldsPanelContent/PublicBoardCustomFieldsPanel';
import { PublicKanbanView } from '~/components/PublicBoard/components/PublicKanbanView/PublicKanbanView';
import {
  PublicBoardGalleryView,
  type PublicBoardGalleryViewProps,
} from '~/components/PublicBoard/PublicBoardGalleryView';
import { PublicBoardTableView } from '~/components/PublicBoard/PublicBoardTableView';
import {
  PublicBoardViewControls,
  type PublicBoardViewControlsProps,
} from '~/components/PublicBoard/PublicBoardViewControls';
import { PublicBoardsAndAssetsSelectionBar } from '~/components/PublicGallery/PublicBoardsAndAssetsSelectionBar';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useShowExpandNestedContentToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useShowExpandNestedContentToggle';
import { usePublicBoardGalleryNestedControls } from '~/components/v2/components/board/gallery/usePublicBoardGalleryNestedControls';
import { GALLERY_NESTED_SECTION_HEADER_HEIGHT } from '~/components/v2/components/shared/gallery/GalleryNestedSectionHeader';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useRerenderOnMount } from '~/hooks/useRerenderOnMount';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import { setConfigurableViewFieldsAction } from '~/store/configViews/actions';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useGetPublicConfigViewOptions } from '~/swr-hooks/gallery/viewFields/useGetPublicConfigViewsOptions';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export type PublicBoardViewProps = {
  headerRef: PublicBoardViewControlsProps['headerRef'];
} & Pick<PublicBoardGalleryViewProps, 'scrollElementRef'>;

export const PublicBoardView = memo(({ scrollElementRef, headerRef }: PublicBoardViewProps) => {
  const boardId = useSelector(CentralizedBoardSelectors.boardId);
  const { permissions } = usePublicPermissions();
  const canViewCustomFields = canSeeBoardCustomFields(permissions);
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const dispatch = useDispatch();
  const { data: configViewOptions } = useGetPublicConfigViewOptions({ viewType: currentViewTypeName });
  const { isSearchActive } = useIsSearchActive();
  const { presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const { showExpandNestedContentToggle } = useShowExpandNestedContentToggle({ isDisabled: isExpandNestedDisabled });
  const { emptyStateHeader, renderHeader } = usePublicBoardGalleryNestedControls();
  // This tree is the public board below the medium breakpoint. It has no desktop header, so the
  // gallery has to carry Expand itself. Without ui2026 the eye button in the controls row stays.
  const galleryHeader = showExpandNestedContentToggle
    ? {
        emptyStateHeader,
        headerContainerHeight: GALLERY_NESTED_SECTION_HEADER_HEIGHT,
        renderHeader,
      }
    : undefined;

  useEffect(() => {
    if (configViewOptions?.data) {
      dispatch(setConfigurableViewFieldsAction({ configurableViewFields: configViewOptions?.data }));
    }
  }, [configViewOptions?.data, dispatch]);

  /**
   * In order to prevent a rehydration issue, we force this page to re-render once after mount.
   * The scenario we're preventing for is that when a user goes directly to a search result url
   * for a public board, on the server side, isSearchActive is false but on first render client side,
   * it's true. So we need to use isMounted checks and also ensure there are 2 renders to fix this.
   */
  const { isMounted } = useRerenderOnMount();

  if (!boardId) {
    return null;
  }

  return (
    <>
      <div className="grow" style={{ height: `calc(100% - ${BOARD_STATS_BAR_HEIGHT}px)` }}>
        {!isSearchActive && isMounted && <PublicBoardHeader />}
        <PublicBoardViewControls headerRef={headerRef} scrollRef={scrollElementRef} />
        {currentViewTypeName === ViewTypeName.gallery && (
          <PublicBoardGalleryView scrollElementRef={scrollElementRef} {...galleryHeader} />
        )}
        {currentViewTypeName === ViewTypeName.table && <PublicBoardTableView scrollElementRef={scrollElementRef} />}
        {currentViewTypeName === ViewTypeName.kanban && <PublicKanbanView />}

        <PublicBoardsAndAssetsSelectionBar />
      </div>
      <PublicBoardStatsBar boardId={boardId} />
      {canViewCustomFields && <PublicBoardCustomFieldsPanel />}
    </>
  );
});

PublicBoardView.displayName = 'PublicBoardView';
